<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import dayjs from 'dayjs';
    import { __ } from '@/js/stores/expressions';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import Accordion from '@/js/components/Accordion.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import DateRangeInput from '@/js/components/DateRangeInput.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import PanelTabs from '@/js/components/PanelTabs.vue';
    import PanelTab from '@/js/components/PanelTab.vue';
    import RadioOption from '@/js/components/RadioOption.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import TextareaInput from '@/js/components/TextareaInput.vue';
    import AsrJobsStatusTable from '../components/AsrJobsStatusTable.vue';
    import SyncJobsTable from '../components/SyncJobsTable.vue';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetAsrConfigFilterOptions,
        useMutationEnqueueAsrFullAnalysis,
        useMutationSendInteractionsForAnalysisByInterval,
        useQueryGetIntervalAnalysisJobs,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { EnqueueAsrFullAnalysisResponse } from '@/js/services/api/backOffice/asrEnqueueApi';
    import type { AsrChannelType } from '@/js/services/api/backOffice/asrConfigApi';

    const successDialogStore = useSuccessDialog();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const { mutateAsync: enqueueMutation, isPending: isEnqueuePending } =
        useMutationEnqueueAsrFullAnalysis();
    const { mutateAsync: intervalMutation, isPending: isIntervalPending } =
        useMutationSendInteractionsForAnalysisByInterval();

    type Mode = 'list' | 'interval';
    const mode = ref<Mode>('interval');

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const interactionPartExternalIdsText = ref('');
    const today = dayjs().format('YYYY-MM-DD');
    const dateRange = ref<{ start?: string; end?: string }>({ start: today, end: today });
    const result = ref<EnqueueAsrFullAnalysisResponse | null>(null);
    const jobsTable = ref<InstanceType<typeof AsrJobsStatusTable> | null>(null);
    const scanJobsTable = ref<InstanceType<typeof SyncJobsTable> | null>(null);

    type AnalysisScope = 'onlyUnanalyzed' | 'reanalyze';
    const analysisScope = ref<AnalysisScope>('onlyUnanalyzed');

    type ChannelMode = 'all' | 'type' | 'specific';
    const channelMode = ref<ChannelMode>('all');
    const selectedChannelIds = ref<string[]>([]);
    const selectedChannelTypes = ref<AsrChannelType[]>([]);

    const { data: filterOptionsData } = useQueryGetAsrConfigFilterOptions(selectedTenantId);

    const toggleChannel = (id: string, checked: boolean) => {
        selectedChannelIds.value = checked
            ? [...selectedChannelIds.value, id]
            : selectedChannelIds.value.filter((c) => c !== id);
    };
    const toggleChannelType = (type: AsrChannelType, checked: boolean) => {
        selectedChannelTypes.value = checked
            ? [...selectedChannelTypes.value, type]
            : selectedChannelTypes.value.filter((t) => t !== type);
    };

    watch(channelMode, (newMode) => {
        if (newMode !== 'specific') selectedChannelIds.value = [];
        if (newMode !== 'type') selectedChannelTypes.value = [];
    });

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const interactionPartExternalIds = computed(() =>
        interactionPartExternalIdsText.value
            .split(/[\n,]/)
            .map((id) => id.trim())
            .filter((id) => id.length > 0)
    );

    const canSubmit = computed(() => {
        if (!selectedTenantId.value) return false;
        if (mode.value === 'list') return interactionPartExternalIds.value.length > 0;
        if (channelMode.value === 'specific' && selectedChannelIds.value.length === 0) return false;
        if (channelMode.value === 'type' && selectedChannelTypes.value.length === 0) return false;
        if (!dateRange.value.start || !dateRange.value.end) return false;
        if (dateRange.value.start > dateRange.value.end) return false;
        return true;
    });

    const isPending = computed(() => isEnqueuePending.value || isIntervalPending.value);

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
        selectedChannelIds.value = [];
        selectedChannelTypes.value = [];
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
        selectedChannelIds.value = [];
        selectedChannelTypes.value = [];
    };

    const submitList = async () => {
        result.value = await enqueueMutation({
            tenantId: selectedTenantId.value,
            interactionPartExternalIds: interactionPartExternalIds.value,
        });

        successDialogStore.openDialog({
            message: __('back-office-asr-analysis-enqueue-success', {
                enqueued: String(result.value.enqueued.length),
                notEligible: String(result.value.notEligible.length),
                notFound: String(result.value.notFound.length),
                alreadyQueued: String(result.value.alreadyQueued.length),
            }),
        });
        jobsTable.value?.refetch();
    };

    const submitInterval = async () => {
        const { id } = await intervalMutation({
            tenantId: selectedTenantId.value,
            start: `${dateRange.value.start}T00:00:00`,
            end: `${dateRange.value.end}T23:59:59`,
            reanalyzeAlreadyAnalyzed: analysisScope.value === 'reanalyze',
            channelIds: channelMode.value === 'specific' ? selectedChannelIds.value : null,
            channelTypes: channelMode.value === 'type' ? selectedChannelTypes.value : null,
        });

        successDialogStore.openDialog({
            message: __('back-office-asr-analysis-enqueue-interval-success', { id }),
        });
        scanJobsTable.value?.refetch();
    };

    const submit = async () => {
        if (!canSubmit.value) return;

        if (mode.value === 'list') {
            await submitList();
        } else {
            await submitInterval();
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">
                {{ __('back-office-asr-analysis-enqueue') }}
            </h1>
        </template>
        <template #content>
            <EchoInfoBox class="mb-4 max-w-2xl">
                {{ __('back-office-asr-analysis-enqueue-description') }}
            </EchoInfoBox>
            <EchoInfoBox class="mb-6 max-w-2xl">
                {{ __('back-office-asr-analysis-enqueue-unanalyzed-warning') }}
            </EchoInfoBox>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="asr-analysis-enqueue-tenant"
                        :label="__('back-office-select-customer')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />

                    <PanelTabs v-model="mode">
                        <PanelTab
                            value="interval"
                            :label="__('back-office-asr-analysis-enqueue-mode-interval')"
                            default
                        >
                            <div class="flex flex-col gap-4">
                                <div class="max-w-xs">
                                    <DateRangeInput v-model="dateRange" />
                                </div>

                                <div class="flex flex-col gap-1">
                                    <p class="text-overline">
                                        {{ __('back-office-asr-analysis-enqueue-scope-heading') }}
                                    </p>
                                    <div class="flex gap-4 flex-wrap">
                                        <RadioOption
                                            id="asr-analysis-enqueue-scope-only-unanalyzed"
                                            v-model="analysisScope"
                                            name="asr-analysis-enqueue-scope"
                                            :label="
                                                __(
                                                    'back-office-asr-analysis-enqueue-scope-only-unanalyzed'
                                                )
                                            "
                                            value="onlyUnanalyzed"
                                        />
                                        <RadioOption
                                            id="asr-analysis-enqueue-scope-reanalyze"
                                            v-model="analysisScope"
                                            name="asr-analysis-enqueue-scope"
                                            :label="
                                                __(
                                                    'back-office-asr-analysis-enqueue-scope-reanalyze'
                                                )
                                            "
                                            value="reanalyze"
                                        />
                                    </div>
                                </div>

                                <div class="flex flex-col gap-2">
                                    <p class="text-overline">
                                        {{
                                            __('back-office-asr-analysis-enqueue-channels-heading')
                                        }}
                                    </p>

                                    <SelectInput
                                        id="asr-analysis-enqueue-channel-mode"
                                        v-model="channelMode"
                                    >
                                        <option value="all">
                                            {{
                                                __('back-office-asr-analysis-enqueue-all-channels')
                                            }}
                                        </option>
                                        <option value="type">
                                            {{ __('back-office-asr-config-option-channel-type') }}
                                        </option>
                                        <option value="specific">
                                            {{ __('back-office-asr-config-option-channel') }}
                                        </option>
                                    </SelectInput>

                                    <Accordion v-if="channelMode === 'type'">
                                        <template #summary>
                                            <span class="flex-1">
                                                <span class="text-overline">
                                                    {{
                                                        __(
                                                            'back-office-asr-config-option-channel-type'
                                                        )
                                                    }}
                                                </span>
                                                <span class="ml-3 text-body text-gray-medium">
                                                    <template
                                                        v-if="selectedChannelTypes.length === 0"
                                                    >
                                                        {{
                                                            __(
                                                                'back-office-asr-config-none-selected'
                                                            )
                                                        }}
                                                    </template>
                                                    <template v-else>
                                                        ({{ selectedChannelTypes.length }})
                                                    </template>
                                                </span>
                                            </span>
                                        </template>
                                        <div class="flex flex-col gap-1 px-5 pb-3">
                                            <CheckboxInput
                                                id="asr-analysis-enqueue-channel-type-audio"
                                                :model-value="
                                                    selectedChannelTypes.includes('Audio')
                                                "
                                                @update:model-value="
                                                    toggleChannelType('Audio', $event ?? false)
                                                "
                                            >
                                                {{
                                                    __('back-office-asr-config-channel-type-audio')
                                                }}
                                            </CheckboxInput>
                                            <CheckboxInput
                                                id="asr-analysis-enqueue-channel-type-text"
                                                :model-value="selectedChannelTypes.includes('Text')"
                                                @update:model-value="
                                                    toggleChannelType('Text', $event ?? false)
                                                "
                                            >
                                                {{ __('back-office-asr-config-channel-type-text') }}
                                            </CheckboxInput>
                                        </div>
                                    </Accordion>

                                    <Accordion v-if="channelMode === 'specific'">
                                        <template #summary>
                                            <span class="flex-1">
                                                <span class="text-overline">
                                                    {{ __('back-office-asr-config-rule-channel') }}
                                                </span>
                                                <span class="ml-3 text-body text-gray-medium">
                                                    <template
                                                        v-if="selectedChannelIds.length === 0"
                                                    >
                                                        {{
                                                            __(
                                                                'back-office-asr-config-none-selected'
                                                            )
                                                        }}
                                                    </template>
                                                    <template v-else>
                                                        ({{ selectedChannelIds.length }})
                                                    </template>
                                                </span>
                                            </span>
                                        </template>
                                        <div class="flex flex-col gap-1 px-5 pb-3">
                                            <CheckboxInput
                                                v-for="channel in filterOptionsData?.channels ?? []"
                                                :id="`asr-analysis-enqueue-channel-${channel.id}`"
                                                :key="channel.id"
                                                :model-value="
                                                    selectedChannelIds.includes(channel.id)
                                                "
                                                @update:model-value="
                                                    toggleChannel(channel.id, $event ?? false)
                                                "
                                            >
                                                {{ channel.name }}
                                            </CheckboxInput>
                                        </div>
                                    </Accordion>
                                </div>
                            </div>
                        </PanelTab>

                        <PanelTab
                            value="list"
                            :label="__('back-office-asr-analysis-enqueue-mode-list')"
                        >
                            <div class="flex flex-col gap-1">
                                <label for="asr-analysis-enqueue-part-ids" class="text-overline">
                                    {{
                                        __('back-office-asr-analysis-enqueue-interaction-part-ids')
                                    }}
                                </label>
                                <TextareaInput
                                    id="asr-analysis-enqueue-part-ids"
                                    v-model="interactionPartExternalIdsText"
                                    :rows="8"
                                />
                            </div>
                        </PanelTab>
                    </PanelTabs>
                </FormGroup>

                <div class="mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{
                            mode === 'list'
                                ? __('back-office-asr-analysis-enqueue-submit')
                                : __('back-office-asr-analysis-enqueue-interval-submit')
                        }}
                    </EchoButton>
                </div>
            </form>

            <template v-if="mode === 'list'">
                <div v-if="result && result.notEligible.length > 0" class="mt-10">
                    <h2 class="heading-sm mb-2">
                        {{ __('back-office-asr-analysis-enqueue-not-eligible-heading') }}
                    </h2>
                    <ul class="list-disc pl-5">
                        <li v-for="id in result.notEligible" :key="id">{{ id }}</li>
                    </ul>
                </div>

                <div v-if="result && result.notFound.length > 0" class="mt-10">
                    <h2 class="heading-sm mb-2">
                        {{ __('back-office-asr-analysis-enqueue-not-found-heading') }}
                    </h2>
                    <ul class="list-disc pl-5">
                        <li v-for="id in result.notFound" :key="id">{{ id }}</li>
                    </ul>
                </div>

                <div v-if="result && result.alreadyQueued.length > 0" class="mt-10">
                    <h2 class="heading-sm mb-2">
                        {{ __('back-office-asr-analysis-enqueue-already-queued-heading') }}
                    </h2>
                    <ul class="list-disc pl-5">
                        <li v-for="id in result.alreadyQueued" :key="id">{{ id }}</li>
                    </ul>
                </div>
            </template>

            <SyncJobsTable
                v-if="mode === 'interval' && selectedTenantId"
                ref="scanJobsTable"
                class="mt-10"
                :use-jobs-query="() => useQueryGetIntervalAnalysisJobs()"
                :heading="__('back-office-interval-heading')"
            />

            <AsrJobsStatusTable
                v-if="selectedTenantId"
                ref="jobsTable"
                class="mt-10"
                :tenant-id="selectedTenantId"
                :heading="__('back-office-interactions-heading')"
            />
        </template>
    </SettingsPageLayout>
</template>
